import { useEffect, useSyncExternalStore } from 'react';
import { registerChatSessionClearer } from '../slices/chat/chatSessionLifecycle';

/**
 * mcgraw-hill: the AI desktop's Clinical / Study mode (ORA-1260). The toggle
 * in the desktop's header writes it (ORA-1265); the desktop's chat transport
 * sends it with each desktop turn, and the backend honours it only for a
 * McGraw Hill member. A Clinical turn finds, suggests and opens no Ora study
 * content.
 *
 * In memory only, so a reload starts on Clinical again. It goes back to
 * Clinical when the account signs out or switches, and when the profile stops
 * being a McGraw Hill member, so the choice never carries into another
 * instance.
 */
export type McGrawHillMode = 'clinical' | 'study';

export const DEFAULT_MCGRAW_HILL_MODE: McGrawHillMode = 'clinical';

let mode: McGrawHillMode = DEFAULT_MCGRAW_HILL_MODE;
const listeners = new Set<() => void>();

export const getMcGrawHillMode = (): McGrawHillMode => mode;

const subscribeMcGrawHillMode = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setMcGrawHillMode = (next: McGrawHillMode): void => {
  if (mode === next) return;
  mode = next;
  listeners.forEach((listener) => listener());
};

export const resetMcGrawHillMode = (): void =>
  setMcGrawHillMode(DEFAULT_MCGRAW_HILL_MODE);

registerChatSessionClearer(resetMcGrawHillMode);

export const useMcGrawHillMode = (): McGrawHillMode =>
  useSyncExternalStore(
    subscribeMcGrawHillMode,
    getMcGrawHillMode,
    getMcGrawHillMode
  );

/**
 * A member in Clinical mode (ORA-1265): their chat offers no Ora study
 * prompts, neither the preset groups nor the chips under an answer. Takes the
 * caller's `useIsMcGrawHill()` so this module needs no instance lookup.
 */
export const useIsMcGrawHillClinical = (isMcGrawHill: boolean): boolean => {
  const mode = useMcGrawHillMode();
  return isMcGrawHill && mode === 'clinical';
};

/** Puts the mode back to Clinical whenever the profile is not a member. */
export const useResetMcGrawHillModeOutsideInstance = (
  isMcGrawHill: boolean
): void => {
  useEffect(() => {
    if (!isMcGrawHill) resetMcGrawHillMode();
  }, [isMcGrawHill]);
};
